// WHAT: Main application component for notifications management
// WHY:  Provides the primary interface for viewing and creating notifications
// DOMAIN: Admin → Notifications
// DEPENDENCIES: useNotifications hook, NotificationItem, CreateNotificationModal
// RISKS:
//   - NOTE: No error handling for addNotification failures
//   - NOTE: Modal closes even if notification creation fails

import { type FC, useState } from "react";

import { CreateNotificationModal } from "./components";
import { useNotifications } from "./hooks";
import { NotificationItem } from "./screen";

/**
 * WHAT:
 *   Main application component that displays notifications list and provides
 *   functionality to create new notifications.
 *
 * WHY:
 *   Serves as the entry point for the notifications feature, orchestrating
 *   data fetching, display, and creation operations.
 *
 * BEHAVIOR:
 *   - Fetches and displays all notifications using useNotifications hook
 *   - Manages modal state for creating new notifications
 *   - Handles notification creation and closes modal after success
 *   - Renders notification items with mark as read functionality
 *
 * RISKS:
 *   - NOTE: handleCreate doesn't handle errors - modal closes even on failure
 *   - NOTE: No loading state during notification creation
 *   - NOTE: No empty state when notifications array is empty
 *
 * @returns {JSX.Element} Die gerenderte NotificationsApp-Komponente
 *
 */
export const NotificationsApp: FC = () => {
  // NOTE: Hook for notifications data and operations
  const { addNotification, markAsRead, notifications } = useNotifications();

  // NOTE: State for modal visibility
  const [modalOpen, setModalOpen] = useState(false);

  /**
   * WHAT:
   *   Handles notification creation and closes the modal.
   *
   * WHY:
   *   Centralizes creation logic and ensures modal closes after operation.
   *
   * BEHAVIOR:
   *   - Calls addNotification with title and message
   *   - Closes modal after operation completes
   *
   * RISKS:
   *   - NOTE: Modal closes even if addNotification fails - add error handling
   *   - NOTE: No user feedback on success or failure
   *
   * @param title
   *
   * @param message
   *
   */
  const handleCreate = async (title: string, message: string) => {
    await addNotification(title, message);
    setModalOpen(false);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Notifications</h1>
        <button
          className="btn-primary"
          onClick={() => {
            setModalOpen(true);
          }}
        >
          Neue Notification
        </button>
      </div>

      <ul className="space-y-2">
        {notifications.map((n) => (
          <NotificationItem
            key={n.id}
            notification={n}
            onRead={() => {
              void markAsRead(n.id);
            }}
          />
        ))}
      </ul>

      {modalOpen ? (
        <CreateNotificationModal
          onClose={() => {
            setModalOpen(false);
          }}
          onCreate={handleCreate}
          open={modalOpen}
        />
      ) : null}
    </div>
  );
};
